// Lightbox controller for Stimulus (Issue #845)
//
// Provides fullscreen image viewing with zoom, pan, and download controls.
// Images with data-action="click->lightbox#open" will open in the lightbox.
//
// @example HTML usage with helper
//   <%= lightbox_image_tag("/path/to/image.jpg", alt: "Description") %>
//
// @example Manual HTML usage
//   <img src="/thumb.jpg"
//        data-action="click->lightbox#open"
//        data-lightbox-src="/full.jpg"
//        data-lightbox-download="/full.jpg">
//
// Keyboard shortcuts:
//   - Escape: Close lightbox
//   - +/=: Zoom in
//   - -: Zoom out

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["image", "download"]

  connect() {
    this.scale = 1
    this.translateX = 0
    this.translateY = 0
    this.isDragging = false
    this.minScale = 0.5
    this.maxScale = 5
    this.scaleStep = 1.25
  }

  // Open lightbox with image from clicked element
  open(event) {
    event.preventDefault()
    const trigger = event.currentTarget
    const src = trigger.dataset.lightboxSrc || trigger.src
    const downloadUrl = trigger.dataset.lightboxDownload || src

    if (!src) return

    this.imageTarget.src = src
    this.downloadTarget.href = downloadUrl
    // .lightbox provides display:flex; d-none (display:none !important) hides it.
    this.element.classList.remove("d-none")
    this.reset()

    // Prevent body scroll when lightbox is open
    document.body.style.overflow = "hidden"
  }

  // Close lightbox and reset state
  close() {
    this.element.classList.add("d-none")
    document.body.style.overflow = ""
    this.reset()
  }

  // Increase zoom level
  zoomIn() {
    this.scale = Math.min(this.scale * this.scaleStep, this.maxScale)
    this.updateTransform()
  }

  // Decrease zoom level
  zoomOut() {
    this.scale = Math.max(this.scale / this.scaleStep, this.minScale)
    this.updateTransform()
  }

  // Reset zoom and position to default
  reset() {
    this.scale = 1
    this.translateX = 0
    this.translateY = 0
    this.updateTransform()
  }

  // Apply current transform values to image
  updateTransform() {
    this.imageTarget.style.transform =
      `scale(${this.scale}) translate(${this.translateX}px, ${this.translateY}px)`
  }

  // Handle keyboard shortcuts
  handleKeydown(event) {
    // Only handle keys when lightbox is visible
    if (this.element.classList.contains("d-none")) return

    switch (event.key) {
      case "Escape":
        this.close()
        break
      case "+":
      case "=":
        event.preventDefault()
        this.zoomIn()
        break
      case "-":
        event.preventDefault()
        this.zoomOut()
        break
    }
  }

  // Close when clicking overlay (not image)
  overlayClick(event) {
    if (event.target === event.currentTarget) {
      this.close()
    }
  }
}
